import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Permission } from '@crm/shared/permissions';
import { EmptyState, ErrorState } from '../components/States.tsx';
import { Skeleton } from '../components/ui/skeleton.tsx';
import { setUnauthorizedHandler } from '../lib/api.ts';
import { useT } from '../lib/i18n.tsx';
import { ME_KEY, useMe } from '../lib/queries.ts';

/** Any 401 from the API means the session ended: forget the user so the guard redirects to login. */
export function useSessionExpiry(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    setUnauthorizedHandler(() => queryClient.setQueryData(ME_KEY, null));
    return () => setUnauthorizedHandler(undefined);
  }, [queryClient]);
}

/** Sends visitors without a session to `/login?next=…`; the shell renders only for a signed-in user. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  const t = useT();
  if (me.isPending)
    return (
      <div className="p-6" aria-busy="true" aria-label={t.common.loading}>
        <Skeleton className="h-8 w-48" />
      </div>
    );
  if (me.isError) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />;
  if (!me.data) {
    const next = location.pathname + location.search;
    return (
      <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />
    );
  }
  return <>{children}</>;
}

/** The page behind this route needs a permission; without it the user sees "no permission". */
export function RequirePermission({ permission }: { permission: Permission }) {
  const me = useMe();
  const t = useT();
  if (!me.data?.permissions.includes(permission))
    return <EmptyState title={t.errors.forbiddenTitle} description={t.errors.forbiddenDetail} />;
  return <Outlet />;
}
